
import React, { useState, useRef, useEffect } from 'react';
import type { GridRow, GridColumn } from '../types/gridTypes';
import type { AIResponse} from '../services/aiService';
import { askGemini, isGeminiConfigured } from '../services/aiService';
import { IconSend, IconSparkles, IconPlus } from './icons';

interface Props {
  rows: GridRow[];
  columns: GridColumn[];
  onApplyAction: (action: any) => void;
}

interface Message {
  id: string;
  sender: 'user' | 'ai';
  text: string;
  timestamp: number;
  pendingAction?: AIResponse['action'];
}

export const SidebarAI: React.FC<Props> = ({ rows, columns, onApplyAction }) => {
  const aiAvailable = isGeminiConfigured;
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      sender: 'ai',
      text: aiAvailable
        ? 'Hello! I am your ProGrid Assistant. I can help you analyze data, create new tasks, or suggest formulas. Try asking: "Add 5 marketing tasks" or "Summarize the budget".'
        : 'AI features are turned off because no Gemini API key is configured. Add GEMINI_API_KEY to your .env.local file and restart the dev server to chat with AI.',
      timestamp: Date.now()
    }
  ]);
  
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading || !aiAvailable) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      sender: 'user',
      text: input,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await askGemini(input, { rows, columns });
      
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: response.text,
        timestamp: Date.now(),
        pendingAction: response.action
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch {
      setMessages(prev => [...prev, {
         id: Date.now().toString(),
         sender: 'ai',
         text: "Sorry, something went wrong. Please try again.",
         timestamp: Date.now()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleApply = (msgId: string, action: NonNullable<AIResponse['action']>) => {
      onApplyAction(action);
      // Remove the action from the message so it can't be applied twice visually (optional)
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, pendingAction: undefined, text: m.text + "\n\n(Action applied ✅)" } : m));
  };

  return (
    <div className="sidebar-panel flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-4" ref={scrollRef}>
        {messages.map(msg => (
          <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}>
            <div className={`max-w-[90%] rounded-lg p-3 text-sm shadow-sm ${
              msg.sender === 'user'
                ? 'sidebar-ai-msg-user rounded-br-none'
                : 'sidebar-ai-msg-ai border rounded-bl-none'
            }`}>
              <div className="whitespace-pre-wrap">{msg.text}</div>

              {msg.pendingAction && (
                  <div className="sidebar-ai-action mt-3 pt-3 border-t">
                      <div className="sidebar-ai-action-header text-xs font-bold mb-2 uppercase tracking-wide flex items-center gap-1">
                         <IconSparkles className="w-3 h-3" />
                         Suggested Action
                      </div>
                      {msg.pendingAction.type === 'ADD_ROWS' && (
                          <div className="sidebar-ai-action-preview mb-2 text-xs p-2 rounded border">
                             Adds {msg.pendingAction.rows.length} new row(s)
                          </div>
                      )}
                      <button
                        onClick={() => handleApply(msg.id, msg.pendingAction!)}
                        className="sidebar-ai-apply-btn w-full py-1.5 text-xs font-bold rounded flex items-center justify-center gap-1 transition-colors"
                      >
                          <IconPlus className="w-3 h-3" />
                          Apply Changes
                      </button>
                  </div>
              )}
            </div>
            <span className="sidebar-ai-timestamp text-[10px] mt-1 px-1">
              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        ))}
        {isLoading && (
           <div className="flex items-start">
             <div className="sidebar-ai-msg-ai border rounded-lg rounded-bl-none p-3 text-sm shadow-sm flex items-center gap-2">
                <IconSparkles className="w-4 h-4 animate-pulse sidebar-ai-loading-icon" />
                Thinking...
             </div>
           </div>
        )}
      </div>

      <div className="sidebar-input-area p-4 border-t">
         <div className="relative flex items-center">
             <textarea
                 value={input}
                 onChange={(e) => setInput(e.target.value)}
                 onKeyDown={handleKeyDown}
                 placeholder={aiAvailable ? "Ask AI to analyze or edit..." : "Add GEMINI_API_KEY to enable AI"}
                 disabled={!aiAvailable}
                 className={`sidebar-textarea w-full border rounded-lg pl-3 pr-10 py-3 text-sm outline-none resize-none ${!aiAvailable ? 'sidebar-ai-disabled' : ''}`}
                rows={1}
                style={{ minHeight: '44px', maxHeight: '120px' }}
             />
             <button
                onClick={handleSend}
                 disabled={!input.trim() || isLoading || !aiAvailable}
                 className={`sidebar-send-btn absolute right-2 p-1.5 rounded-md transition-colors ${(!input.trim() || isLoading || !aiAvailable) ? 'disabled' : ''}`}
             >
                <IconSend className="w-5 h-5" />
             </button>
         </div>
            <div className={`sidebar-ai-status text-[10px] text-center mt-2 ${!aiAvailable ? 'sidebar-ai-warning' : ''}`}>
              {aiAvailable ? 'Gemini 2.5 Flash • AI can make mistakes.' : 'AI disabled • Set GEMINI_API_KEY to enable Gemini 2.5 Flash.'}
            </div>
      </div>
    </div>
  );
};
